import { BotController } from '../controllers/BotController.ts';
import type { Controller } from '../controllers/Controller.ts';
import { EmgController } from '../controllers/EmgController.ts';
import { ExternalButtonController } from '../controllers/ExternalButtonController.ts';
import { KeyboardController, NullController } from '../controllers/KeyboardController.ts';
import { MouseController } from '../controllers/MouseController.ts';
import { SimulatedStimulationController } from '../controllers/SimulatedStimulationController.ts';
import { StimulationController } from '../controllers/StimulationController.ts';
import { CalibrationSession, type CalibrationSummary } from '../experiments/Calibration.ts';
import { EventType } from '../experiments/Events.ts';
import { ExperimentSession } from '../experiments/ExperimentSession.ts';
import type { Replay } from '../experiments/Replay.ts';
import { Renderer } from '../game/Renderer.ts';
import type { InputDevice } from '../hardware/InputDevice.ts';
import { KeyboardButtonDevice } from '../hardware/KeyboardButtonDevice.ts';
import { SerialInput } from '../hardware/SerialInput.ts';
import { SimulatedEmgDevice } from '../hardware/SimulatedEmgDevice.ts';
import { VirtualButtonDevice } from '../hardware/VirtualButtonDevice.ts';
import { WebSocketInput } from '../hardware/WebSocketInput.ts';
import { BackyardBrainsHHIAdapter } from '../stimulation/BackyardBrainsHHIAdapter.ts';
import { SIMULATED_SAFETY_LIMITS } from '../stimulation/SafetyGate.ts';
import { MockStimulator } from '../stimulation/MockStimulator.ts';
import { SimulatedHumanStimulator } from '../stimulation/SimulatedHumanStimulator.ts';
import { CalibrationPanel } from '../ui/CalibrationPanel.ts';
import { ComparisonPanel } from '../ui/ComparisonPanel.ts';
import { ControlPanel } from '../ui/ControlPanel.ts';
import { button, checkbox, downloadText, el, select } from '../ui/dom.ts';
import { ExperimentDashboard } from '../ui/ExperimentDashboard.ts';
import { ReplayPanel } from '../ui/ReplayPanel.ts';
import { SafetyBar } from '../ui/SafetyBar.ts';
import { clearSavedConfig, loadConfig, saveConfig, type AppConfig, type InputDeviceId } from './Config.ts';
import { getMode, MODES, type ModeId } from './Modes.ts';

/**
 * Application shell: builds the modes, owns the animation loop, and connects
 * the panels to the session.
 *
 * Everything experimental lives elsewhere. This file exists so that the game,
 * the controllers, the stimulators, and the logging can each be read on their
 * own without a UI framework in the way — and so that the mode table is in one
 * readable place, because "which of these seven arrangements am I actually
 * running?" is the question a researcher asks most often.
 */
export class App {
  readonly config: AppConfig;
  readonly session: ExperimentSession;

  private renderer: Renderer;
  private canvas: HTMLCanvasElement;

  private safetyBar: SafetyBar;
  private dashboard: ExperimentDashboard;
  private controlPanel: ControlPanel;
  private calibrationPanel: CalibrationPanel;
  private comparisonPanel: ComparisonPanel;
  private replayPanel: ReplayPanel;
  private toastEl: HTMLElement;
  private advancedSettings: HTMLDetailsElement;

  // Per-mode pieces. Null when the current mode does not use them.
  private bot: BotController | null = null;
  private stimController: StimulationController | null = null;
  private inputDevice: InputDevice | null = null;
  private virtualButton: VirtualButtonDevice | null = null;
  private simulatedHuman: SimulatedHumanStimulator | null = null;
  private simulatedEmg: SimulatedEmgDevice | null = null;
  private calibration: CalibrationSession | null = null;
  /** Dev stand-in that injects bursts into the simulated EMG signal. */
  private emgTrigger: KeyboardButtonDevice | null = null;
  private unregisterPumps: (() => void)[] = [];

  private replay: Replay | null = null;
  private rafHandle = 0;
  private looping = false;
  private frameTimes: number[] = [];
  private lastFrameAt = 0;
  private switching = false;
  private won = false;

  constructor(private readonly mount: HTMLElement) {
    this.config = loadConfig();

    this.session = new ExperimentSession({
      game: this.config.game,
      safety: this.config.safety,
      logger: { attachGameState: this.config.logGameState },
    });

    // ---------------------------------------------------------------- layout
    const layout = el('div', 'app');
    const stage = el('div', 'stage');
    this.canvas = el('canvas', 'game-canvas');
    stage.appendChild(this.canvas);
    this.toastEl = el('div', 'toast');
    stage.appendChild(this.toastEl);

    this.safetyBar = new SafetyBar(this.session.stimulation, {
      onArm: () => void this.arm(),
      onDisarm: () => void this.disarm(),
      onEmergencyStop: () => this.emergencyStop('operator'),
      onClearStop: () => {
        this.session.stimulation.clearEmergencyStop(this.session.engine.currentTick);
        this.toast('Emergency stop cleared. Re-arm explicitly to enable stimulation.');
      },
    });

    this.dashboard = new ExperimentDashboard({
      session: this.session,
      controller: () => this.session.controller,
      bot: () => this.bot,
      stimController: () => this.stimController,
      fps: () => this.fps(),
      modeLabel: () => getMode(this.config.mode).label,
    });

    this.controlPanel = new ControlPanel(this.config, {
      onModeChange: (mode) => void this.setMode(mode),
      onRestart: () => this.restart(),
      onTogglePause: () => this.togglePause(),
      onConfigChanged: () => this.applyConfig(),
      onConnectDevice: () => void this.connectInputDevice(),
      onConnectStimulator: () => void this.connectStimulator(),
      onExportJSON: () => this.exportJSON(),
      onExportCSV: () => this.exportCSV(),
      onResetConfig: () => {
        clearSavedConfig();
        this.toast('Settings cleared. Reload the page to start from defaults.');
      },
    });

    this.calibrationPanel = new CalibrationPanel(
      () => this.calibration,
      {
        onStart: () => void this.startCalibration(),
        onStop: () => this.calibration?.stop(),
        onApply: (summary) => this.applyCalibration(summary),
      },
    );

    this.comparisonPanel = new ComparisonPanel({
      onBeforeRun: () => this.stopLoop(),
      onAfterRun: () => this.startLoop(),
      getGameConfig: () => ({ ...this.config.game }),
    });

    this.replayPanel = new ReplayPanel({
      getCurrentExport: () => this.session.logger.toJSON(),
      onReplayChanged: (replay) => {
        this.replay = replay;
        if (replay) this.session.setPaused(true);
      },
    });

    const left = el('div', 'column column-left');
    left.append(this.replayPanel.root, this.comparisonPanel.root);

    const middle = el('div', 'column column-middle');
    middle.append(this.dashboard.root, this.calibrationPanel.root);

    const right = el('div', 'column column-right');
    right.appendChild(this.controlPanel.root);

    layout.append(left, middle, right);

    const toolbar = el('div', 'simple-toolbar');
    const modeSelect = select(
      MODES.map((mode) => ({ value: mode.id, label: mode.label })),
      this.config.mode,
      (mode) => void this.setMode(mode),
    );
    modeSelect.classList.add('simple-mode');
    const startBtn = button('START', () => void this.startGame(), 'btn-start');
    const stopBtn = button('STOP', () => void this.stopGame(), 'btn-stop');
    const restartBtn = button('RESTART', () => this.restartGame(), 'btn-secondary');
    const settingsBtn = button('SETTINGS', () => {
      this.advancedSettings.open = !this.advancedSettings.open;
    }, 'btn-secondary');
    toolbar.append(modeSelect, startBtn, stopBtn, restartBtn, settingsBtn);

    this.advancedSettings = el('details', 'advanced-settings');
    const quickSettings = el('div', 'quick-settings');
    quickSettings.append(
      checkbox('Show bot guide lines', this.config.showBotVision, (visible) => {
        this.config.showBotVision = visible;
        this.applyConfig();
      }),
    );
    this.advancedSettings.append(
      el('summary', 'advanced-summary', 'Settings — click to close'),
      quickSettings,
      this.safetyBar.root,
      layout,
    );

    const gameShell = el('main', 'game-shell');
    gameShell.append(stage);
    this.mount.append(toolbar, gameShell, this.advancedSettings);

    this.renderer = new Renderer(this.canvas, this.session.engine.config);

    // Autonomous control must stop the instant stimulation does.
    this.session.stimulation.onEmergencyStop(() => {
      this.controlPanel.setPaused(true);
      this.toast('EMERGENCY STOP — stimulator disarmed, autonomous control paused.');
    });

    window.addEventListener('resize', () => this.renderer.resize());
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('beforeunload', () => saveConfig(this.config));
  }

  async start(): Promise<void> {
    // Simulation by default: a MockStimulator, connected but disarmed, which
    // can produce no output of any kind. Nothing here reaches hardware.
    await this.session.stimulation.setStimulator(new MockStimulator());
    await this.session.stimulation.connect();
    await this.setMode(this.config.mode);
    this.session.reset(this.config.game.seed);
    this.startLoop();
  }

  // ------------------------------------------------------------------- modes

  /**
   * Tear down the current arrangement and build the requested one.
   *
   * Always disarms and disconnects first. Switching modes must never leave a
   * previously armed stimulator live behind a controller that no longer exists.
   */
  async setMode(mode: ModeId): Promise<void> {
    if (this.switching) return;
    this.switching = true;
    try {
      this.config.mode = mode;
      const def = getMode(mode);

      await this.teardownMode();

      this.session.logger.log(EventType.MODE_CHANGED, {
        tick: this.session.engine.currentTick,
        metadata: { mode, label: def.label },
      });

      let controller: Controller;
      switch (mode) {
        case 'keyboard':
          controller = new KeyboardController();
          break;

        case 'mouse':
          controller = new MouseController(this.canvas);
          break;

        case 'direct-bot':
          this.bot = new BotController(this.session.engine.config, {
            ...this.config.bot,
            // Direct control has no actuator, so there is nothing to compensate
            // for. Leaving a stale latency here would make the bot fly ahead of
            // a delay that does not exist.
            latencyMs: 0,
          });
          controller = this.bot;
          break;

        case 'simulated-human': {
          // Start from a perfectly calibrated bot: the assumed actuator latency
          // is the simulated participant's own mean. That is the condition a
          // real session reaches only after running calibration, and it is the
          // baseline the "turn compensation off" demonstration is measured
          // against. The slider is right there to change it.
          this.config.bot.latencyMs = this.config.human.meanLatencyMs;
          this.bot = new BotController(this.session.engine.config, this.config.bot);
          this.virtualButton = new VirtualButtonDevice();
          this.simulatedEmg = new SimulatedEmgDevice(this.config.simulatedEmg);
          await this.simulatedEmg.connect();
          this.simulatedHuman = new SimulatedHumanStimulator(
            this.virtualButton, this.config.human, this.simulatedEmg,
          );
          await this.session.stimulation.setStimulator(this.simulatedHuman);
          await this.session.stimulation.connect(this.session.engine.currentTick);
          this.inputDevice = this.virtualButton;
          this.stimController = new SimulatedStimulationController(
            this.bot, this.session.stimulation, this.virtualButton, this.simulatedHuman,
            this.session.logger, () => this.session.engine.currentTick,
            this.config.stimController, this.simulatedEmg,
          );
          controller = this.stimController;
          this.pump((now) => this.simulatedHuman?.pump(now));
          this.pump((now) => this.simulatedEmg?.pump(now));
          this.toast(
            `Bot latency estimate set to ${this.config.human.meanLatencyMs} ms ` +
              '(the simulated participant’s mean). Arm to begin.',
          );
          break;
        }

        case 'physical-button': {
          this.inputDevice = this.makeInputDevice(this.config.buttonDevice);
          controller = new ExternalButtonController(
            this.inputDevice, this.session.logger, () => this.session.engine.currentTick,
          );
          break;
        }

        case 'emg': {
          this.inputDevice = this.makeInputDevice(this.config.emgDevice);
          if (this.inputDevice instanceof SimulatedEmgDevice) {
            this.simulatedEmg = this.inputDevice;
            this.pump((now) => this.simulatedEmg?.pump(now));
            // Without electrodes there is nothing to contract, so a key stands
            // in for the muscle: pressing it injects a burst into the synthetic
            // signal, which then has to cross the threshold detector like any
            // other. The detector, envelope, and refractory logic are the real
            // ones -- only the muscle is faked.
            this.emgTrigger = new KeyboardButtonDevice(this.config.keyboardButtonKey);
            await this.emgTrigger.connect();
            this.emgTrigger.onButton((e) => {
              if (e.down) this.simulatedEmg?.burst(e.timestamp);
            });
          }
          controller = new EmgController(
            this.inputDevice, this.session.logger,
            () => this.session.engine.currentTick, this.config.emg,
          );
          break;
        }

        case 'hhi-human': {
          this.bot = new BotController(this.session.engine.config, this.config.bot);
          this.inputDevice = this.makeInputDevice(this.config.buttonDevice);
          const adapter = new BackyardBrainsHHIAdapter(this.config.hhi);
          await this.session.stimulation.setStimulator(adapter);
          this.stimController = new StimulationController(
            this.bot, this.session.stimulation, this.inputDevice,
            this.session.logger, () => this.session.engine.currentTick,
            this.config.stimController,
            'hhi-stimulation', 'Bot → HHI → participant → switch',
          );
          controller = this.stimController;
          this.toast(
            'HHI mode selected. Nothing is connected or armed. ' +
              'Connect the stimulator and the input device explicitly.',
          );
          break;
        }

        default:
          controller = new NullController();
      }

      if (!def.usesStimulation && !(this.session.stimulation.stimulator instanceof MockStimulator)) {
        await this.session.stimulation.setStimulator(new MockStimulator());
        await this.session.stimulation.connect(this.session.engine.currentTick);
      }

      try {
        await this.session.setController(controller);
      } catch (err) {
        // A hardware device that refuses to connect must not leave the app
        // without a controller; fall back to something inert and say so.
        this.toast(`Could not attach ${controller.label}: ${String(err)}`);
        await this.session.setController(new NullController());
      }

      if (def.usesStimulation) {
        this.calibration = new CalibrationSession(
          this.session.stimulation,
          this.inputDevice ?? new VirtualButtonDevice('unused'),
          this.session.logger,
          () => this.session.engine.currentTick,
        );
        this.pump((now) => this.calibration?.pump(now));
      }

      this.applyConfig();
      this.controlPanel.refresh();
      this.restart();

      // A stimulation mode cannot fly the bird until an operator arms it, and
      // an unflown bird hits the ground in under a second. So the game waits,
      // paused, with the reason on the canvas -- rather than presenting a dead
      // bird and no explanation of why nothing responded.
      if (def.usesStimulation) {
        this.session.setPaused(true);
        this.controlPanel.setPaused(true);
      }
    } finally {
      this.switching = false;
    }
  }

  private async teardownMode(): Promise<void> {
    this.calibration?.stop('mode change');
    this.calibration = null;
    for (const off of this.unregisterPumps) off();
    this.unregisterPumps = [];
    await this.session.stimulation.disarm(this.session.engine.currentTick);
    this.setScrollLocked(false);
    await this.session.controller.detach?.();
    if (this.inputDevice && this.inputDevice !== this.virtualButton) {
      await this.inputDevice.disconnect().catch(() => undefined);
    }
    if (this.emgTrigger) {
      await this.emgTrigger.disconnect();
      this.emgTrigger = null;
    }
    this.bot = null;
    this.stimController = null;
    this.inputDevice = null;
    this.virtualButton = null;
    this.simulatedHuman = null;
    this.simulatedEmg = null;
  }

  private pump(fn: (nowMs: number) => void): void {
    this.unregisterPumps.push(this.session.registerPump(fn));
  }

  private makeInputDevice(id: InputDeviceId): InputDevice {
    switch (id) {
      case 'serial':
        return new SerialInput();
      case 'websocket':
        return new WebSocketInput({ url: this.config.websocketUrl });
      case 'simulated-emg':
        return new SimulatedEmgDevice(this.config.simulatedEmg);
      case 'keyboard-key':
      default:
        return new KeyboardButtonDevice(this.config.keyboardButtonKey);
    }
  }

  // ------------------------------------------------------------- connections

  /** WebSerial requires a user gesture, so connecting is always a button. */
  private async connectInputDevice(): Promise<void> {
    if (!this.inputDevice) return;
    try {
      await this.inputDevice.connect();
      this.toast(`Connected: ${this.inputDevice.label}`);
    } catch (err) {
      this.toast(`Could not connect input device: ${String(err)}`);
    }
  }

  private async connectStimulator(): Promise<void> {
    try {
      await this.session.stimulation.connect(this.session.engine.currentTick);
      this.toast(
        `Stimulator connected: ${this.session.stimulation.getStatus().label}. Still DISARMED.`,
      );
    } catch (err) {
      this.toast(`Could not connect stimulator: ${String(err)}`);
    }
  }

  private async startGame(): Promise<void> {
    const gate = this.session.stimulation.gate.getState();
    if (gate.emergencyStopped) {
      this.session.stimulation.clearEmergencyStop(this.session.engine.currentTick);
    }
    if (!this.session.stimulation.getStatus().connected) {
      await this.connectStimulator();
    }
    if (!this.session.stimulation.getStatus().connected) return;
    await this.arm();
  }

  private async stopGame(): Promise<void> {
    await this.disarm();
    this.session.setPaused(true);
    this.controlPanel.setPaused(true);
    this.toast('Game stopped.');
  }

  private restartGame(): void {
    this.restart();
    if (getMode(this.config.mode).usesStimulation && !this.session.stimulation.getStatus().armed) {
      this.session.setPaused(true);
      this.controlPanel.setPaused(true);
    }
  }

  /** Start a run. Connection is the setup step; this is the single go action. */
  private async arm(): Promise<void> {
    const status = this.session.stimulation.getStatus();
    try {
      await this.session.stimulation.arm(this.session.engine.currentTick);
      this.toast(status.simulated ? 'Game started (simulated).' : 'Game started.');
      this.setScrollLocked(true);
      if (getMode(this.config.mode).usesStimulation) this.restart();
    } catch (err) {
      this.toast(`Could not arm: ${String(err)}`);
    }
  }

  private async disarm(): Promise<void> {
    await this.session.stimulation.disarm(this.session.engine.currentTick);
    this.setScrollLocked(false);
  }

  private emergencyStop(reason: string): void {
    this.session.stimulation.emergencyStop(reason, this.session.engine.currentTick);
    this.calibration?.stop('emergency stop');
    this.setScrollLocked(false);
  }

  private setScrollLocked(locked: boolean): void {
    document.documentElement.classList.toggle('game-running', locked);
    document.body.classList.toggle('game-running', locked);
  }

  // ------------------------------------------------------------------ config

  /** Push the edited config into the live objects. */
  private applyConfig(): void {
    Object.assign(this.session.engine.config, this.config.game);

    // Which limit set binds depends on what is on the other end. Real hardware
    // always gets the operator's limits; a simulated actuator gets them only if
    // the rate limiter is itself the object of study.
    const simulated = this.session.stimulation.getStatus().simulated;
    Object.assign(
      this.session.stimulation.gate.limits,
      simulated && !this.config.enforceSafetyLimitsInSimulation
        ? SIMULATED_SAFETY_LIMITS
        : this.config.safety,
    );
    this.session.logger.options.attachGameState = this.config.logGameState;
    this.renderer.theme = this.config.renderTheme;
    this.session.stimulation.defaultDurationMs = this.config.stimController.stimulusDurationMs;

    if (this.bot) {
      const mediated = getMode(this.config.mode).usesStimulation;
      // Asking faster than the safety layer will allow just fills the log with
      // rejections and leaves the bot believing in impulses that were never
      // dispatched. The gate's refractory period is a floor on the bot's own
      // intent interval, plus one tick so the two do not race at the boundary.
      const gateFloor = mediated
        ? this.session.stimulation.gate.limits.minIntervalMs + 1000 / this.config.game.simHz
        : 0;
      this.bot.params = {
        ...this.config.bot,
        latencyMs: this.config.mode === 'direct-bot' ? 0 : this.config.bot.latencyMs,
        minIntentIntervalMs: Math.max(this.config.bot.minIntentIntervalMs, gateFloor),
      };
    }
    if (this.simulatedHuman) Object.assign(this.simulatedHuman.model, this.config.human);
    if (this.stimController) Object.assign(this.stimController.options, this.config.stimController);
    const controller = this.session.controller;
    if (controller instanceof EmgController) Object.assign(controller.params, this.config.emg);

    saveConfig(this.config);
  }

  private applyCalibration(summary: CalibrationSummary): void {
    this.config.bot.latencyMs = summary.suggestedLatencyMs;
    this.config.bot.inFlightGraceMs = summary.suggestedGraceMs;
    this.config.bot.minIntentIntervalMs = Math.max(
      this.config.bot.minIntentIntervalMs,
      summary.suggestedMinIntervalMs,
    );
    this.config.stimController.responseTimeoutMs = Math.round(summary.p95Ms + 150);
    this.applyConfig();
    this.controlPanel.refresh();
    this.session.logger.log(EventType.CONFIG_CHANGED, {
      tick: this.session.engine.currentTick,
      metadata: { appliedCalibration: summary },
    });
    this.toast(
      `Applied: latency ${summary.suggestedLatencyMs} ms, grace ${summary.suggestedGraceMs} ms, ` +
        `min interval ${summary.suggestedMinIntervalMs} ms.`,
    );
  }

  // -------------------------------------------------------------- game state

  private restart(): void {
    this.won = false;
    this.session.reset(this.config.game.seed);
    this.session.setPaused(false);
    this.controlPanel.setPaused(false);
  }

  private togglePause(): void {
    this.session.setPaused(!this.session.paused);
    this.controlPanel.setPaused(this.session.paused);
  }

  private onKeyDown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (target && /INPUT|TEXTAREA|SELECT/.test(target.tagName)) return;

    if (e.code === 'Escape') {
      this.emergencyStop('escape key');
      e.preventDefault();
      return;
    }
    // Space is the only gameplay key. It also restarts a finished run so the
    // operator never needs a second gameplay shortcut.
    if (!this.session.engine.isAlive && e.code === 'Space') {
      this.restart();
      e.preventDefault();
    }
  }

  // ------------------------------------------------------------------- loop

  private startLoop(): void {
    if (this.looping) return;
    this.looping = true;
    const frame = (nowMs: number) => {
      if (!this.looping) return;
      this.frame(nowMs);
      this.rafHandle = requestAnimationFrame(frame);
    };
    this.rafHandle = requestAnimationFrame(frame);
  }

  private stopLoop(): void {
    this.looping = false;
    cancelAnimationFrame(this.rafHandle);
  }

  private frame(nowMs: number): void {
    if (this.lastFrameAt > 0) {
      this.frameTimes.push(nowMs - this.lastFrameAt);
      if (this.frameTimes.length > 60) this.frameTimes.shift();
    }
    this.lastFrameAt = nowMs;

    if (this.replay) {
      this.replay.advance(nowMs);
      const state = this.replay.getState();
      this.renderer.render(
        state,
        {
          showBotVision: false,
          banner: null,
          replayLabel: `REPLAY ${this.replay.speed.toFixed(2)}×`,
          fps: this.fps(),
        },
        nowMs,
      );
      this.replayPanel.sync(false);
      this.dashboard.update(state, nowMs);
      this.safetyBar.update();
      return;
    }

    if (this.waitingForArm && !this.session.paused) {
      this.session.setPaused(true);
      this.controlPanel.setPaused(true);
    }

    const before = this.session.engine.totalFlaps;
    this.session.advance(nowMs);
    if (this.session.engine.totalFlaps > before) this.renderer.noteFlap(nowMs);

    const state = this.session.engine.getState();
    if (state.score >= 10 && !this.won) void this.finishWin();
    this.renderer.render(
      state,
      {
        decision: this.bot?.lastDecision ?? null,
        openRequests: this.stimController?.openRequestCount ?? 0,
        banner: this.banner(state.alive),
        subBanner: this.subBanner(state.alive),
        fps: this.fps(),
        showBotVision: this.config.showBotVision,
      },
      nowMs,
    );

    this.dashboard.update(state, nowMs);
    this.safetyBar.update();
    this.calibrationPanel.update();
  }

  /** True when this mode needs stimulation and has not been given permission. */
  private get waitingForArm(): boolean {
    return getMode(this.config.mode).usesStimulation && !this.session.stimulation.gate.isArmed;
  }

  private banner(alive: boolean): string | null {
    if (this.session.stimulation.gate.isEmergencyStopped) return 'EMERGENCY STOP';
    if (this.won) return 'GAME OVER';
    if (this.waitingForArm) return 'DISARMED';
    if (!alive) return 'GAME OVER';
    if (this.session.paused) return 'PAUSED';
    return null;
  }

  private subBanner(alive: boolean): string | null {
    if (this.session.stimulation.gate.isEmergencyStopped) {
      return 'Clear the stop, then re-arm';
    }
    if (this.won) return 'YOU WIN!';
    if (this.waitingForArm) {
      return this.session.stimulation.getStatus().connected
        ? 'Arm the stimulator to begin — nothing can flap until then'
        : 'Connect the stimulator, then arm it';
    }
    if (!alive) return 'Space to restart';
    if (this.session.paused) return 'P to resume';
    return null;
  }

  private async finishWin(): Promise<void> {
    this.won = true;
    this.session.setPaused(true);
    this.controlPanel.setPaused(true);
    await this.disarm();
    this.toast('Game over — you win!');
  }

  private fps(): number {
    if (this.frameTimes.length === 0) return 0;
    const mean = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    return mean > 0 ? 1000 / mean : 0;
  }

  // ------------------------------------------------------------------ export

  private stamp(): string {
    return `${this.session.sessionId}-${this.config.mode}`;
  }

  private exportJSON(): void {
    this.session.logger.setMetadata('config', this.config);
    downloadText(`${this.stamp()}.json`, this.session.logger.toJSONString(true), 'application/json');
  }

  private exportCSV(): void {
    downloadText(`${this.stamp()}.csv`, this.session.logger.toCSV(), 'text/csv');
  }

  private toastTimer = 0;

  private toast(message: string): void {
    this.toastEl.textContent = message;
    this.toastEl.classList.add('visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('visible'), 6000);
  }

  private async startCalibration(): Promise<void> {
    if (!this.calibration) return;
    try {
      await this.calibration.start();
      this.session.setPaused(true);
      this.controlPanel.setPaused(true);
      this.toast('Calibration running. The game is paused.');
    } catch (err) {
      this.toast(String(err));
    }
  }
}
